import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs/operators';

import { DiagnosticEventType } from '../models/diagnostic-profile';
import { DiagnosticBufferService } from './diagnostic-buffer.service';

/**
 * Pushes a RouteEvent to the diagnostic buffer on every Router
 * NavigationEnd. The service is `providedIn: 'root'` but must be
 * eagerly instantiated (e.g. injected in `AppComponent`'s
 * constructor) so the subscription is live before the user starts
 * navigating.
 *
 * Only NavigationEnd is captured — the post-redirect resolved URL.
 * NavigationStart, NavigationCancel and NavigationError are out of
 * scope for now; they can be added later if the support team needs
 * to see in-flight or failed navigations.
 */
@Injectable({ providedIn: 'root' })
export class RouteHistoryService {
  constructor(
    private readonly router: Router,
    private readonly buffer: DiagnosticBufferService
  ) {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.buffer.push({
          type: DiagnosticEventType.ROUTE,
          timestamp: Date.now(),
          // The buffer overrides `profile` with the active profile name;
          // this placeholder is just for type satisfaction.
          profile: 'default',
          payload: {
            url: event.urlAfterRedirects,
            navigationId: event.id,
          },
        });
      });
  }
}
